import { useEffect, useReducer, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/* ============================================================
   CreativeHeroPipeline — full-width animated workspace replacing
   the generic Pexels video on /creative.
   Cycles through 4 stages of a creative production pipeline:
     01 · Sketch    — design canvas with components drawing on
     02 · Compose   — multi-up grid of moodboard imagery
     03 · Motion    — animated typography reveal (kinetic type)
     04 · Live      — final website preview with parallax cursor
   Sits behind the hero overlay copy. Uses the same #0B0F14
   base + yellow accents + film grain so the existing overlay
   gradients still read perfectly.
============================================================ */

const STAGE_DURATIONS = {
  sketch: 4000,
  compose: 3800,
  motion: 4000,
  live: 4500,
};
const STAGES = ["sketch", "compose", "motion", "live"];

/* ------------------------------------------------------------
   Stage 01 · Sketch — design canvas
------------------------------------------------------------ */
const SketchStage = () => {
  const fade = (delay, y = 6) => ({
    initial: { opacity: 0, y },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
  });
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Subtle dotted canvas grid behind the Figma window */}
      <span
        aria-hidden
        className="absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.04) 1px, transparent 0)",
          backgroundSize: "28px 28px",
        }}
      />

      {/* FIGMA WINDOW — anchored upper-right so the foreground
          headline (lower-left) stays the dominant visual. */}
      <motion.div
        initial={{ opacity: 0, y: 18, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="absolute top-[10%] right-[4%] md:right-[6%] w-[58%] md:w-[55%] aspect-[16/10] rounded-2xl overflow-hidden ring-1 ring-white/[0.10] bg-[#0F141A] shadow-[0_70px_140px_-30px_rgba(0,0,0,0.85),0_30px_60px_-20px_rgba(244,214,78,0.14),0_0_0_1px_rgba(255,255,255,0.04)_inset]"
      >
        {/* ───── Figma top toolbar ───── */}
        <div className="h-7 flex items-center gap-2 px-2.5 bg-[#0F141A] border-b border-white/[0.06]">
          <span className="w-3 h-3 rounded-[3px] bg-gradient-to-br from-[#F4D64E] to-[#FF7AB6] shrink-0" />
          <span className="text-[0.5rem] font-bold tracking-[0.18em] uppercase text-neutral-300 truncate">
            maison-sunday <span className="text-neutral-600">/ home — v3.4</span>
          </span>
          <span className="hidden md:inline-flex ml-2 px-1.5 py-0.5 rounded-sm bg-white/[0.06] border border-white/[0.06] text-[0.42rem] font-bold tracking-[0.14em] uppercase text-neutral-400">
            2 min ago
          </span>
          <span className="ml-auto flex items-center gap-1">
            {[
              { l: "V", a: true },
              { l: "F", a: false },
              { l: "T", a: false },
            ].map((b, i) => (
              <span
                key={i}
                className={`w-4 h-4 rounded-sm flex items-center justify-center text-[0.5rem] font-bold ${
                  b.a
                    ? "bg-[#F4D64E] text-neutral-900"
                    : "bg-white/[0.06] text-neutral-400 border border-white/[0.05]"
                }`}
              >
                {b.l}
              </span>
            ))}
            <span className="ml-1.5 px-1.5 py-0.5 rounded-sm bg-[#F4D64E] text-neutral-900 text-[0.45rem] font-bold tracking-[0.16em] uppercase">
              Share
            </span>
          </span>
        </div>

        {/* ───── Body — left layers / canvas / right inspector ───── */}
        <div className="flex h-[calc(100%-1.75rem)]">
          {/* LAYERS */}
          <motion.div
            {...fade(0.15, 0)}
            className="hidden md:block w-[22%] shrink-0 bg-[#0F141A] border-r border-white/[0.06] py-2 px-1.5 overflow-hidden"
          >
            <div className="text-[0.42rem] font-bold tracking-[0.24em] uppercase text-neutral-500 mb-1.5 px-1">
              Pages
            </div>
            {["Home", "Atelier", "Journal", "Lookbook"].map((p, i) => (
              <div
                key={p}
                className={`text-[0.5rem] py-0.5 px-1.5 rounded-sm flex items-center gap-1.5 ${
                  i === 0 ? "bg-[#F4D64E]/[0.10] text-[#F4D64E]" : "text-neutral-400"
                }`}
              >
                <span className="w-1 h-1 rounded-[1px] bg-current opacity-60" />
                {p}
              </div>
            ))}
            <div className="mt-2 text-[0.42rem] font-bold tracking-[0.24em] uppercase text-neutral-500 mb-1 px-1">
              Layers
            </div>
            {[
              { l: "Frame · Hero", d: 0 },
              { l: "Image · Editorial", d: 1 },
              { l: "Text · Headline", d: 1 },
              { l: "Button · CTA", d: 1 },
              { l: "Frame · Nav", d: 0 },
              { l: "Frame · Product", d: 0 },
              { l: "Frame · Footer", d: 0 },
            ].map((row) => (
              <div
                key={row.l}
                className="text-[0.48rem] py-0.5 text-neutral-400 flex items-center gap-1 truncate"
                style={{ paddingLeft: `${0.4 + row.d * 0.55}rem` }}
              >
                <span className="w-1 h-1 rounded-full bg-neutral-500 shrink-0" />
                <span className="truncate">{row.l}</span>
              </div>
            ))}
          </motion.div>

          {/* CANVAS */}
          <div className="relative flex-1 bg-[#1A1F26] overflow-hidden"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.05) 1px, transparent 0)",
              backgroundSize: "20px 20px",
            }}
          >
            {/* Drafting eyebrow */}
            <motion.div
              {...fade(0.25, 0)}
              className="absolute top-2 left-2 text-[0.42rem] font-bold tracking-[0.28em] uppercase text-[#F4D64E] inline-flex items-center gap-1.5"
            >
              <span className="w-2.5 h-px bg-[#F4D64E]" />
              Drafting · v0.1
            </motion.div>

            {/* Frame label */}
            <motion.div
              {...fade(0.35, 0)}
              className="absolute top-[14%] left-[10%] text-[0.42rem] font-bold tracking-[0.20em] uppercase text-[#F4D64E]"
            >
              Hero — 1440×900
            </motion.div>

            {/* HOMEPAGE FRAME — being assembled live */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="absolute top-[20%] left-[10%] right-[8%] bottom-[16%] rounded-md bg-[#FAF8F3] ring-2 ring-[#F4D64E] ring-offset-2 ring-offset-[#1A1F26] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] overflow-hidden"
            >
              {/* Tiny browser bar */}
              <div className="h-3 flex items-center gap-1 px-1.5 border-b border-black/[0.05] bg-[#EFEBE3]">
                <span className="w-1 h-1 rounded-full bg-[#FF5F57]" />
                <span className="w-1 h-1 rounded-full bg-[#FEBC2E]" />
                <span className="w-1 h-1 rounded-full bg-[#28C840]" />
                <span className="ml-1.5 text-[0.32rem] font-mono text-neutral-500 truncate">
                  maisonsunday.io
                </span>
              </div>
              {/* Nav strip */}
              <motion.div
                initial={{ opacity: 0, y: -3 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.55 }}
                className="h-3.5 flex items-center justify-between px-1.5 border-b border-black/[0.05]"
              >
                <span
                  style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
                  className="text-[0.42rem] font-bold tracking-[0.10em] uppercase text-neutral-900"
                >
                  MAISON SUNDAY
                </span>
                <span className="hidden lg:flex items-center gap-1.5 text-[0.32rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
                  <span>Atelier</span>
                  <span>·</span>
                  <span>Bag</span>
                </span>
              </motion.div>
              {/* Hero composition */}
              <div className="relative h-[calc(100%-1.7rem)] grid grid-cols-12 gap-1.5 p-1.5">
                <motion.div
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.65, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
                  className="col-span-7 relative rounded-sm overflow-hidden bg-neutral-200"
                >
                  <img
                    src="https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1600&q=75"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                  <motion.span
                    aria-hidden
                    className="absolute inset-y-0 -left-1/2 w-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent skew-x-[-12deg]"
                    initial={{ x: "-50%" }}
                    animate={{ x: "300%" }}
                    transition={{ duration: 1.1, delay: 0.9, ease: "linear" }}
                  />
                  <span className="absolute bottom-0.5 left-1 text-[0.32rem] font-bold tracking-[0.28em] uppercase text-white/85">
                    ÉD III
                  </span>
                </motion.div>
                <div className="col-span-5 relative flex flex-col justify-end pr-1">
                  <motion.div
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 1.0 }}
                    style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
                    className="text-neutral-900 font-light leading-[0.92] tracking-[-0.018em]"
                  >
                    <span className="block" style={{ fontSize: "0.85rem" }}>Made</span>
                    <span className="italic block" style={{ fontSize: "0.85rem" }}>to outlast</span>
                    <span className="block" style={{ fontSize: "0.85rem" }}>the season.</span>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 1.35 }}
                    className="mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 bg-neutral-900 text-white text-[0.36rem] font-bold tracking-[0.22em] uppercase rounded-sm self-start"
                  >
                    Discover edition
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4, delay: 1.55 }}
                    className="absolute -left-1 bottom-2 inline-flex items-center"
                  >
                    <span className="w-2 h-px bg-[#FF7AB6]" />
                    <span className="ml-0.5 text-[0.32rem] font-mono tabular-nums text-[#FF7AB6]">
                      24 px
                    </span>
                  </motion.div>
                </div>
              </div>

              {/* Selection handles */}
              {[
                { x: "0", y: "0" },
                { x: "100%", y: "0" },
                { x: "0", y: "100%" },
                { x: "100%", y: "100%" },
              ].map((p, i) => (
                <motion.span
                  key={i}
                  aria-hidden
                  initial={{ opacity: 0, scale: 0.6 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: 0.5 + i * 0.05 }}
                  className="absolute w-1.5 h-1.5 rounded-[1px] bg-[#F4D64E] ring-1 ring-[#0B0F14]"
                  style={{ left: p.x, top: p.y, transform: "translate(-50%,-50%)" }}
                />
              ))}
            </motion.div>

            {/* COMMENT BUBBLE */}
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.5, delay: 1.7, ease: [0.22, 1, 0.36, 1] }}
              className="absolute right-[6%] top-[24%] flex items-start gap-1.5"
            >
              <span className="shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#FF7AB6] text-[0.42rem] font-bold tracking-[0.04em] text-white shadow-[0_4px_12px_-2px_rgba(255,122,182,0.5)]">
                SD
              </span>
              <span className="rounded-md bg-white text-neutral-900 text-[0.45rem] leading-tight px-1.5 py-1 max-w-[8rem] shadow-[0_8px_24px_-6px_rgba(0,0,0,0.4)]">
                <span className="block font-bold">More breathing room.</span>
                <span className="block text-[0.36rem] tracking-[0.18em] uppercase text-neutral-500 mt-0.5">
                  02:47
                </span>
              </span>
            </motion.div>

            {/* TOKENS strip */}
            <motion.div
              {...fade(1.85, 6)}
              className="absolute bottom-2 left-[10%] flex items-center gap-1.5"
            >
              <span className="text-[0.36rem] font-bold tracking-[0.28em] uppercase text-neutral-500">
                Tokens
              </span>
              {[
                { c: "bg-[#F4D64E]", l: "Y" },
                { c: "bg-[#FF7AB6]", l: "P" },
                { c: "bg-[#0B0F14] border border-white/[0.20]", l: "INK" },
                { c: "bg-white", l: "PAPER" },
              ].map((t) => (
                <span
                  key={t.l}
                  className="inline-flex items-center gap-1 text-[0.36rem] font-bold tracking-[0.16em] uppercase text-neutral-400"
                >
                  <span className={`w-2.5 h-2.5 rounded-[2px] ${t.c}`} />
                  {t.l}
                </span>
              ))}
            </motion.div>

            {/* Cursor */}
            <motion.div
              aria-hidden
              initial={{ opacity: 0, x: "10%", y: "92%" }}
              animate={{
                opacity: [0, 1, 1, 1, 1],
                x: ["10%", "26%", "26%", "60%", "78%"],
                y: ["92%", "60%", "40%", "40%", "30%"],
              }}
              transition={{ duration: 2.6, ease: [0.22, 1, 0.36, 1], times: [0, 0.2, 0.5, 0.75, 1] }}
              className="absolute w-3 h-3 z-30"
            >
              <svg viewBox="0 0 16 22" className="w-3 h-3.5">
                <path
                  d="M 0 0 L 0 18 L 5 14 L 9 22 L 12 20 L 8 13 L 14 12 Z"
                  fill="#F4D64E"
                  stroke="#0B0F14"
                  strokeWidth="0.6"
                />
              </svg>
              <span className="absolute top-2.5 left-2 px-1 py-px rounded-sm bg-[#F4D64E] text-neutral-900 text-[0.32rem] font-bold tracking-[0.16em] uppercase whitespace-nowrap">
                Sunday
              </span>
            </motion.div>
          </div>

          {/* INSPECTOR */}
          <motion.div
            {...fade(0.2, 0)}
            className="hidden md:block w-[24%] shrink-0 bg-[#0F141A] border-l border-white/[0.06] py-2 px-1.5 overflow-hidden"
          >
            <div className="text-[0.42rem] font-bold tracking-[0.24em] uppercase text-neutral-500 mb-1.5">
              Frame
            </div>
            <div className="grid grid-cols-2 gap-1 mb-2">
              <span className="rounded-sm bg-white/[0.04] border border-white/[0.06] py-0.5 px-1 text-[0.45rem] text-neutral-300 tabular-nums">
                W <span className="ml-0.5 text-white">1440</span>
              </span>
              <span className="rounded-sm bg-white/[0.04] border border-white/[0.06] py-0.5 px-1 text-[0.45rem] text-neutral-300 tabular-nums">
                H <span className="ml-0.5 text-white">900</span>
              </span>
            </div>
            <div className="text-[0.42rem] font-bold tracking-[0.24em] uppercase text-neutral-500 mb-1">
              Fill
            </div>
            <div className="rounded-sm bg-white/[0.04] border border-white/[0.06] p-1 mb-2 flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#0B0F14] border border-white/[0.10]" />
              <span className="text-[0.45rem] tabular-nums text-neutral-300">#0B0F14</span>
            </div>
            <div className="text-[0.42rem] font-bold tracking-[0.24em] uppercase text-neutral-500 mb-1">
              Type
            </div>
            <div className="rounded-sm bg-white/[0.04] border border-white/[0.06] p-1 mb-1 text-[0.45rem] text-neutral-300">
              Cabinet Grotesk
            </div>
            <div className="rounded-sm bg-white/[0.04] border border-white/[0.06] p-1 text-[0.45rem] text-neutral-300">
              72 / 1.0 / -2%
            </div>
            <div className="mt-2 text-[0.42rem] font-bold tracking-[0.24em] uppercase text-neutral-500 mb-1">
              Effects
            </div>
            <div className="rounded-sm bg-white/[0.04] border border-white/[0.06] p-1 mb-1 text-[0.45rem] text-neutral-300 flex items-center gap-1">
              <span className="w-1 h-1 rounded-full bg-[#F4D64E]" />
              Drop · 24
            </div>
            <div className="rounded-sm bg-white/[0.04] border border-white/[0.06] p-1 text-[0.45rem] text-neutral-300 flex items-center gap-1">
              <span className="w-1 h-1 rounded-full bg-[#82E1FF]" />
              Blur · 6
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Vertical "FIGMA · DRAFTING" mark on the far right */}
      <div className="absolute right-1 top-[28%] hidden md:flex items-center gap-2 text-[0.55rem] font-bold tracking-[0.32em] uppercase text-neutral-500 [writing-mode:vertical-rl] rotate-180">
        <span className="w-6 h-px bg-[#F4D64E]" />
        ● DESIGN · DRAFT 03
      </div>
    </div>
  );
};

/* ------------------------------------------------------------
   Stage 02 · Compose — moodboard grid
------------------------------------------------------------ */
const COMPOSE_TILES = [
  { src: "/images/creative/two-women-red.png", span: "col-span-3 row-span-2", tag: "EDITORIAL" },
  { src: "/images/creative/cafe-joyeux.jpg", span: "col-span-2 row-span-1", tag: "BRAND" },
  { src: "/images/creative/veronique-roses.jpg", span: "col-span-2 row-span-1", tag: "STORY" },
  { src: "/images/creative/veronique-pool.jpg", span: "col-span-2 row-span-2", tag: "PRODUCT" },
  { src: "/images/creative/veronique-back.jpg", span: "col-span-3 row-span-1", tag: "LIFESTYLE" },
  { src: "/images/creative/cafe-joyeux.jpg", span: "col-span-2 row-span-1", tag: "PACKAGING" },
];

const ComposeStage = () => (
  <div className="absolute inset-0">
    {/* Side label */}
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      className="absolute top-[8%] right-[5%] text-[0.6rem] font-bold tracking-[0.32em] uppercase text-[#F4D64E]"
    >
      <span className="inline-flex items-center gap-2">
        <span className="w-6 h-px bg-[#F4D64E]" />
        Moodboard · Issue 02
      </span>
    </motion.div>

    {/* Grid anchored to upper-right so the foreground headline
        (lower-left) reads cleanly. */}
    <div className="absolute top-[14%] right-[4%] w-[58%] md:w-[55%] aspect-[7/4] grid grid-cols-7 grid-rows-3 gap-2.5">
      {COMPOSE_TILES.map((tile, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 16, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            duration: 0.7,
            delay: 0.15 + i * 0.12,
            ease: [0.22, 1, 0.36, 1],
          }}
          className={`${tile.span} relative overflow-hidden rounded-md ring-1 ring-white/10`}
          style={{ backgroundImage: `url(${tile.src})`, backgroundSize: "cover", backgroundPosition: "center" }}
        >
          <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
          <span className="absolute bottom-1.5 left-1.5 text-[0.45rem] font-bold tracking-[0.3em] uppercase text-white/95">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-px bg-[#F4D64E]" />
              {tile.tag}
            </span>
          </span>
        </motion.div>
      ))}
    </div>
  </div>
);

/* ------------------------------------------------------------
   Stage 03 · Motion — cinematic editorial video showcase.
   Plays the brand-shot Westman Atelier × Bergdorf Goodman reel
   inside a refined floating frame with deep shadow, ambient
   side-glow, vertical "REEL" mark, and scrubber timeline.
   The video is offset to the upper-right so the page headline
   in the lower-left stays crisp.
------------------------------------------------------------ */
const MOTION_VIDEO_SRC_MP4 = "/videos/creative-motion-reel.mp4";
const MOTION_VIDEO_SRC_WEBM = "/videos/creative-motion-reel.webm";

const MotionStage = () => {
  // Seek to a strong middle frame once metadata is ready,
  // so the visible window starts on the most cinematic beat
  // (intro/outro slates avoided).
  const ref = useRef(null);
  const onLoadedMetadata = (e) => {
    const v = e.currentTarget;
    try {
      const d = v.duration;
      if (d && Number.isFinite(d)) {
        v.currentTime = Math.max(0, d * 0.45);
      }
      v.play().catch(() => {});
    } catch (_e) {
      /* noop */
    }
  };

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Ambient backdrop — subtle warm spotlight behind the frame */}
      <span
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(700px 380px at 75% 32%, rgba(244,214,78,0.12), transparent 60%)",
        }}
      />
      <span
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(900px 480px at 28% 78%, rgba(255,122,182,0.07), transparent 65%)",
        }}
      />

      {/* CINEMATIC FRAME — anchored upper-right so the page
          headline (lower-left) remains the dominant visual. */}
      <motion.div
        initial={{ opacity: 0, y: 18, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="absolute top-[12%] right-[5%] md:right-[7%] w-[55%] md:w-[50%] aspect-[16/9] rounded-2xl overflow-hidden ring-1 ring-white/[0.10] shadow-[0_70px_140px_-30px_rgba(0,0,0,0.85),0_30px_60px_-20px_rgba(244,214,78,0.18),0_0_0_1px_rgba(255,255,255,0.04)_inset]"
      >
        {/* Slim film strip — top */}
        <div className="absolute top-0 inset-x-0 h-2.5 bg-[#0B0F14]/95 z-10 flex items-center justify-between px-2">
          <span className="text-[0.42rem] font-bold tracking-[0.32em] uppercase text-[#F4D64E] flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-[#F4D64E] animate-pulse" />
            REC · 4K
          </span>
          <span className="hidden md:inline text-[0.4rem] font-mono tabular-nums text-neutral-500">
            ISO 400 · 24FPS · 1.85:1
          </span>
        </div>

        {/* The video */}
        <video
          ref={ref}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onLoadedMetadata={onLoadedMetadata}
          className="absolute inset-0 w-full h-full object-cover"
        >
          {/* Browser picks the first compatible source.
              MP4/H.264 covers Chrome, Safari, Firefox, Edge.
              WebM/VP9 covers headless Chromium without H.264. */}
          <source src={MOTION_VIDEO_SRC_MP4} type="video/mp4" />
          <source src={MOTION_VIDEO_SRC_WEBM} type="video/webm" />
        </video>

        {/* Editorial vignettes — left + bottom + top for cinematic finish */}
        <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/30" />
        <span aria-hidden className="absolute inset-0 bg-gradient-to-r from-black/35 via-transparent to-transparent" />

        {/* Top-left chapter mark */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="absolute top-4 left-3 text-[0.5rem] font-bold tracking-[0.32em] uppercase text-white/85 z-10 inline-flex items-center gap-2"
        >
          <span className="w-3 h-px bg-[#F4D64E]" />
          Reel · Beauty 02
        </motion.div>

        {/* Top-right runtime/info chip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.6 }}
          className="absolute top-4 right-3 z-10 inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-sm bg-black/40 backdrop-blur-sm border border-white/[0.10] text-[0.42rem] font-bold tracking-[0.22em] uppercase text-white/85"
        >
          <span className="w-1 h-1 rounded-full bg-[#FF5F57] animate-pulse" />
          ON AIR
        </motion.div>

        {/* Slim film strip — bottom (timecode + scrubber) */}
        <div className="absolute bottom-0 inset-x-0 h-3.5 bg-[#0B0F14]/95 z-10 flex items-center gap-2 px-2">
          <span className="text-[0.42rem] font-mono tabular-nums text-[#F4D64E]">
            00:14
          </span>
          <span className="flex-1 h-px bg-white/[0.10] relative overflow-hidden">
            <motion.span
              className="absolute inset-y-0 left-0 bg-[#F4D64E]"
              initial={{ width: "32%" }}
              animate={{ width: ["32%", "64%", "32%"] }}
              transition={{ duration: 9, ease: "linear", repeat: Infinity }}
            />
          </span>
          <span className="text-[0.42rem] font-mono tabular-nums text-neutral-400">
            00:43
          </span>
        </div>
      </motion.div>

      {/* Vertical "REEL · 26" mark on the far right */}
      <div className="absolute right-2 top-[30%] hidden md:flex items-center gap-2 text-[0.55rem] font-bold tracking-[0.32em] uppercase text-neutral-500 [writing-mode:vertical-rl] rotate-180">
        <span className="w-6 h-px bg-[#F4D64E]" />
        ● MOTION REEL · SS 26
      </div>

      {/* Soft floor reflection beneath the frame */}
      <span
        aria-hidden
        className="absolute right-[5%] md:right-[7%] top-[calc(12%+_28vw)] md:top-[calc(12%+_28.125vw)] w-[55%] md:w-[50%] h-12 rounded-[50%] blur-2xl bg-black/70 -z-10"
      />
    </div>
  );
};

/* ------------------------------------------------------------
   Stage 04 · Live — final website preview with cursor parallax
------------------------------------------------------------ */
const LIVE_FRAMES = [
  {
    label: "ATELIER · LOOKBOOK",
    src: "/images/creative/two-women-red.png",
  },
  {
    label: "STUDIO · PRODUCT",
    src: "/images/creative/veronique-pool.jpg",
  },
];

const LiveStage = () => (
  <div className="absolute inset-0 overflow-hidden">
    {/* Browser frame anchored to upper-right so the foreground
        headline (lower-left) stays clear. */}
    <motion.div
      initial={{ opacity: 0, y: 18, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="absolute top-[10%] right-[4%] md:right-[6%] w-[55%] md:w-[52%] aspect-[16/9] rounded-xl overflow-hidden ring-1 ring-white/[0.10] shadow-[0_60px_120px_-30px_rgba(0,0,0,0.7),0_30px_60px_-20px_rgba(244,214,78,0.18)]"
    >
      {/* Browser chrome (sticky) */}
      <div className="flex items-center gap-1.5 h-7 px-3 border-b border-white/[0.06] bg-[#0F141A]/95 z-20 relative">
        <span className="w-1.5 h-1.5 rounded-full bg-[#FF5F57]" />
        <span className="w-1.5 h-1.5 rounded-full bg-[#FEBC2E]" />
        <span className="w-1.5 h-1.5 rounded-full bg-[#28C840]" />
        <span className="ml-3 text-[0.55rem] font-mono text-neutral-400 truncate tracking-wider">
          maisonsunday.io
        </span>
        <span className="ml-auto text-[0.5rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E]">
          ● LIVE
        </span>
      </div>

      {/* Scroll viewport — auto-pans through the delivered page */}
      <div className="relative h-[calc(100%-1.75rem)] overflow-hidden bg-[#F5F2EC]">
        {/* Right-edge scroll-progress indicator (yellow) */}
        <span
          aria-hidden
          className="absolute top-2 bottom-2 right-1 w-px bg-black/[0.08] z-20"
        >
          <motion.span
            className="absolute left-[-1.5px] w-[3px] h-5 rounded-full bg-[#F4D64E]"
            initial={{ y: 0 }}
            animate={{ y: ["0%", "calc(100% - 20px)"] }}
            transition={{ duration: 4.2, ease: [0.45, 0, 0.55, 1] }}
          />
        </span>

        {/* Tall delivered-page that scrolls */}
        <motion.div
          initial={{ y: 0 }}
          animate={{ y: ["0%", "-58%"] }}
          transition={{ duration: 4.2, ease: [0.45, 0, 0.55, 1] }}
          className="absolute inset-x-0 top-0"
        >
          {/* ── Page nav strip ── */}
          <div className="flex items-center justify-between px-3 pt-2.5 pb-2 border-b border-black/[0.05] bg-[#F5F2EC]">
            <span
              style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
              className="text-[0.7rem] font-bold tracking-[0.14em] uppercase text-neutral-900"
            >
              MAISON SUNDAY
            </span>
            <span className="hidden sm:flex items-center gap-2.5 text-[0.4rem] font-bold tracking-[0.28em] uppercase text-neutral-600">
              <span>Collection</span>
              <span>Atelier</span>
              <span>Journal</span>
            </span>
            <span className="flex items-center gap-1.5 text-[0.4rem] font-bold tracking-[0.28em] uppercase text-neutral-600">
              <span>FR · EN</span>
              <span>·</span>
              <span>Bag · 0</span>
            </span>
          </div>

          {/* ── HERO (lookbook image with editorial title) ── */}
          <div className="relative mx-3 mt-3 h-[280px] rounded-sm overflow-hidden bg-neutral-200">
            <img
              src={LIVE_FRAMES[0].src}
              alt={LIVE_FRAMES[0].label}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/15" />

            <div className="absolute top-3 left-3 right-3 flex items-center justify-between text-[0.4rem] font-bold tracking-[0.3em] uppercase text-white/80">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-3 h-px bg-[#F4D64E]" />
                ATELIER · COLLECTION III
              </span>
              <span className="text-white">01 · 04</span>
            </div>

            <div className="absolute left-3 right-3 bottom-3 max-w-[80%]">
              <div className="text-[0.4rem] font-bold tracking-[0.3em] uppercase text-white/80 mb-1">
                Lookbook 02 · Spring 26
              </div>
              <div
                style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
                className="text-white font-light leading-[0.98] tracking-[-0.018em]"
              >
                <span className="block" style={{ fontSize: "1.55rem" }}>An object made</span>
                <span className="italic block" style={{ fontSize: "1.55rem" }}>to be lived in.</span>
              </div>
              <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-white text-neutral-900 text-[0.4rem] font-bold tracking-[0.28em] uppercase rounded-sm">
                Discover the edition
                <span className="w-1 h-1 rounded-full bg-[#F4D64E]" />
              </div>
            </div>
          </div>

          {/* ── EDITORIAL split — image + serif copy ── */}
          <div className="px-3 mt-5 grid grid-cols-12 gap-2 items-stretch">
            <div className="col-span-5 relative aspect-[4/5] bg-neutral-200 overflow-hidden rounded-sm">
              <img
                src={LIVE_FRAMES[1].src}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
              <span className="absolute bottom-1.5 left-1.5 text-[0.36rem] font-bold tracking-[0.32em] uppercase text-white/95 inline-flex items-center gap-1">
                <span className="w-2 h-px bg-white/80" />
                Studio · Paris
              </span>
            </div>
            <div className="col-span-7 flex flex-col justify-center pl-1">
              <span className="text-[0.4rem] font-bold tracking-[0.32em] uppercase text-neutral-500 inline-flex items-center gap-1.5">
                <span className="w-3 h-px bg-[#F4D64E]" />
                The Atelier · Spring 26
              </span>
              <div
                style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
                className="mt-1.5 text-neutral-900 font-light leading-[1.05] tracking-[-0.018em]"
              >
                <span className="block" style={{ fontSize: "1.05rem" }}>Slow craft,</span>
                <span className="block italic" style={{ fontSize: "1.05rem" }}>kept honest.</span>
              </div>
              <p className="mt-2 text-[0.55rem] leading-[1.55] text-neutral-700">
                Each piece is patterned and finished by hand in our 11ème workshop — a deliberate practice in a fast world.
              </p>
              <span className="mt-2 inline-flex items-center gap-1.5 text-[0.4rem] font-bold tracking-[0.28em] uppercase text-neutral-900">
                Read the journal <span aria-hidden>↗</span>
              </span>
            </div>
          </div>

          {/* ── COLLECTION GRID ── */}
          <div className="px-3 mt-5">
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-[0.4rem] font-bold tracking-[0.32em] uppercase text-neutral-700 inline-flex items-center gap-1.5">
                <span className="w-3 h-px bg-[#F4D64E]" />
                The Collection
              </span>
              <span className="text-[0.36rem] font-bold tracking-[0.28em] uppercase text-neutral-500">
                See all ↗
              </span>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { l: "Atelier", n: "Coat Nº01", img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=900&q=70" },
                { l: "Leather", n: "Bag Nº07", img: "https://images.unsplash.com/photo-1591561954557-26941169b49e?auto=format&fit=crop&w=900&q=70" },
                { l: "Studio", n: "Object Nº01", img: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=900&q=70" },
              ].map((p) => (
                <div
                  key={p.n}
                  className="relative aspect-[3/4] overflow-hidden bg-neutral-200 rounded-sm ring-1 ring-black/[0.04]"
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${p.img})` }}
                  />
                  <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
                  <div className="absolute bottom-1.5 left-1.5 right-1.5">
                    <div className="text-[0.34rem] font-bold tracking-[0.3em] uppercase text-white/85">
                      {p.l}
                    </div>
                    <div
                      style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
                      className="text-[0.65rem] leading-none text-white font-light"
                    >
                      {p.n}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── PRESS strip ── */}
          <div className="mt-5 mx-3 py-1.5 border-y border-black/[0.06] flex items-center gap-2.5 overflow-hidden">
            <span className="shrink-0 text-[0.36rem] font-bold tracking-[0.32em] uppercase text-neutral-500">
              As seen in
            </span>
            {["VOGUE", "WALLPAPER*", "ELLE", "AD", "MR PORTER"].map((p, i) => (
              <span
                key={p}
                style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
                className={`text-[0.6rem] font-bold tracking-[0.16em] uppercase ${
                  i % 2 === 0 ? "text-neutral-900" : "text-neutral-700"
                } shrink-0`}
              >
                {p}
              </span>
            ))}
          </div>

          {/* ── CLOSING CTA / NEWSLETTER ── */}
          <div className="mx-3 mt-4 mb-4 rounded-sm bg-neutral-900 p-3 flex items-center justify-between">
            <div>
              <div
                style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
                className="text-white font-light leading-[1.05] tracking-[-0.018em]"
              >
                <span className="block" style={{ fontSize: "0.95rem" }}>The Sunday letter.</span>
              </div>
              <div className="mt-0.5 text-[0.42rem] font-bold tracking-[0.24em] uppercase text-neutral-400">
                Editions, ateliers, journal — once a season.
              </div>
            </div>
            <div className="inline-flex items-center gap-1 px-2 py-1.5 bg-[#F4D64E] text-neutral-900 text-[0.4rem] font-bold tracking-[0.28em] uppercase rounded-sm">
              Subscribe ↗
            </div>
          </div>

          {/* ── FOOTER ── */}
          <div className="mx-3 pt-2 pb-3 border-t border-black/[0.06] flex items-baseline justify-between text-[0.36rem] font-bold tracking-[0.32em] uppercase text-neutral-500">
            <span>Sunday Studio · since 2019</span>
            <span>● Free shipping over € 200</span>
          </div>
        </motion.div>

        {/* "Live page" status overlay — fades subtly during scroll */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.9, 0.9, 0] }}
          transition={{ duration: 4.2, times: [0, 0.1, 0.7, 1] }}
          className="absolute top-2 right-3 z-30 text-[0.36rem] font-bold tracking-[0.28em] uppercase text-neutral-700 inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm px-1.5 py-0.5 rounded-sm border border-black/[0.06]"
        >
          <span className="w-1 h-1 rounded-full bg-[#F4D64E] animate-pulse" />
          Auto-scroll
        </motion.div>
      </div>
    </motion.div>

    {/* Side caption — vertical "REEL · 26" mark on the far right edge */}
    <div className="absolute right-1 top-[14%] hidden md:flex items-center gap-2 text-[0.55rem] font-bold tracking-[0.32em] uppercase text-neutral-500 [writing-mode:vertical-rl] rotate-180">
      <span className="w-6 h-px bg-[#F4D64E]" />
      ● LIVE · COLLECTION 03
    </div>
  </div>
);

/* ------------------------------------------------------------
   Reducer + main
------------------------------------------------------------ */
const STAGE_LABELS = {
  sketch: "01 · Sketch",
  compose: "02 · Compose",
  motion: "03 · Motion",
  live: "04 · Live",
};

const initial = { stage: "sketch" };
const reducer = (s, a) => {
  if (a.type === "set") return { stage: a.stage };
  return s;
};

const StageLabel = ({ stage }) => (
  <div className="absolute top-6 right-6 md:top-8 md:right-10 z-30 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#0B0F14]/70 backdrop-blur-md border border-white/[0.10] text-[0.58rem] font-bold tracking-[0.28em] uppercase text-[#F4D64E]">
    <span className="w-1.5 h-1.5 rounded-full bg-[#F4D64E] animate-pulse" />
    <AnimatePresence mode="wait">
      <motion.span
        key={stage}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.25 }}
      >
        {STAGE_LABELS[stage]}
      </motion.span>
    </AnimatePresence>
  </div>
);

export default function CreativeHeroPipeline() {
  const wrapperRef = useRef(null);
  const [state, dispatch] = useReducer(reducer, initial);
  const playingRef = useRef(true);

  useEffect(() => {
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      dispatch({ type: "set", stage: "live" });
      playingRef.current = false;
      return;
    }

    let timers = [];
    let alive = true;

    const runStage = (idx) => {
      if (!alive || !playingRef.current) return;
      const stage = STAGES[idx % STAGES.length];
      dispatch({ type: "set", stage });
      timers.push(setTimeout(() => runStage(idx + 1), STAGE_DURATIONS[stage]));
    };

    let io;
    if ("IntersectionObserver" in window && wrapperRef.current) {
      io = new IntersectionObserver(
        (entries) => {
          const inView = entries.some((e) => e.isIntersecting);
          if (inView && !playingRef.current) {
            playingRef.current = true;
            runStage(0);
          } else if (!inView) {
            playingRef.current = false;
            timers.forEach(clearTimeout);
            timers = [];
          }
        },
        { threshold: 0.15 }
      );
      io.observe(wrapperRef.current);
    }

    runStage(0);
    return () => {
      alive = false;
      timers.forEach(clearTimeout);
      if (io) io.disconnect();
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      data-testid="creative-hero-pipeline"
      className="absolute inset-0 overflow-hidden"
    >
      {/* Base bg matches the previous video bed */}
      <div className="absolute inset-0 bg-[#0B0F14]" />

      <AnimatePresence mode="wait">
        {state.stage === "sketch" ? (
          <motion.div
            key="sketch"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0"
          >
            <SketchStage />
          </motion.div>
        ) : null}
        {state.stage === "compose" ? (
          <motion.div
            key="compose"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0"
          >
            <ComposeStage />
          </motion.div>
        ) : null}
        {state.stage === "motion" ? (
          <motion.div
            key="motion"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0"
          >
            <MotionStage />
          </motion.div>
        ) : null}
        {state.stage === "live" ? (
          <motion.div
            key="live"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0"
          >
            <LiveStage />
          </motion.div>
        ) : null}
      </AnimatePresence>

      <StageLabel stage={state.stage} />

      {/* Stage progress dots */}
      <div className="absolute bottom-[10%] left-1/2 -translate-x-1/2 z-30 hidden md:flex items-center gap-3">
        {STAGES.map((s) => {
          const active = state.stage === s;
          return (
            <div key={s} className="flex items-center gap-2">
              <span
                className={`h-px transition-all duration-500 ${
                  active ? "w-8 bg-[#F4D64E]" : "w-4 bg-white/[0.20]"
                }`}
              />
              <span
                className={`text-[0.55rem] font-bold tracking-[0.3em] uppercase tabular-nums transition-colors duration-500 ${
                  active ? "text-[#F4D64E]" : "text-neutral-500"
                }`}
              >
                {STAGE_LABELS[s]}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
